import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useBlockSlot,
  SectionContextProvider,
  EditableToken,
} from '@vibesites/runtime/react';
import { ContactMap } from './ContactMap';

// ============================================================================
// Component
// ============================================================================

export function ContactForm() {
  const formSlot = useBlockSlot('contactform.form_block', {
    defaultValue: 'multihub-lead-form',
  });

  return (
    <>
      {/* 50/50 split — map on the left, form on the right */}
      <div className="cf-split-section">
        <div className="cf-split-container">
          <div className="cf-split-grid">
            {/* Left: map */}
            <div className="cf-split-map">
              <ContactMap />
            </div>

            {/* Right: form */}
            <div className="cf-split-form">
              <div className="cf-form-wrap">
                <EditableToken path="contactform.form_block" as="div">
                  {formSlot ? (
                    <SectionContextProvider sectionId={formSlot.sectionId}>
                      <formSlot.BlockComponent sectionId={formSlot.sectionId} />
                    </SectionContextProvider>
                  ) : (
                    <div className="cf-form-empty-card">
                      <p className="cf-form-empty">No form block selected</p>
                    </div>
                  )}
                </EditableToken>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        /* ── 50/50 split section ── */
        .cf-split-section {
          background: white;
          padding: 2rem 0;
        }
        @media (min-width: 768px) {
          .cf-split-section { padding: 3.5rem 0; }
        }
        .cf-split-container {
          max-width: 80rem;
          margin: 0 auto;
          padding: 0 1.5rem;
        }
        @media (min-width: 1024px) {
          .cf-split-container { padding: 0 2rem; }
        }

        .cf-split-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.5rem;
          align-items: stretch;
        }
        @media (min-width: 768px) {
          .cf-split-grid { grid-template-columns: 1fr 1fr; gap: 2rem; }
        }

        /* On mobile, surface the form first (primary action), map below */
        @media (max-width: 767px) {
          .cf-split-form { order: 1; }
          .cf-split-map  { order: 2; }
        }

        /* Map fills its column and matches the form's height on desktop */
        .cf-split-map {
          display: flex;
          border-radius: var(--vibe-radius-lg, 12px);
          overflow: hidden;
          box-shadow: 0 6px 28px rgba(0, 0, 0, 0.12), 0 2px 6px rgba(0, 0, 0, 0.06);
        }
        .cf-split-map .cmap-section { width: 100%; display: flex; }
        .cf-split-map .cmap-iframe,
        .cf-split-map .cmap-placeholder {
          height: 100%;
          min-height: 420px;
        }

        .cf-split-form {
          display: flex;
        }
        .cf-form-wrap {
          width: 100%;
          border-radius: var(--vibe-radius-lg, 12px);
          overflow: hidden;
          box-shadow: 0 6px 28px rgba(0, 0, 0, 0.12), 0 2px 6px rgba(0, 0, 0, 0.06);
        }
        .cf-form-empty-card {
          background: white;
          border-radius: var(--vibe-radius-lg, 12px);
          padding: 2rem;
        }
        .cf-form-empty {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          color: var(--vibe-color-text-muted);
          text-align: center;
          margin: 0;
          padding: 2rem 0;
        }

        /* Page hero already says "Contact Us" — hide the form's duplicate
           title (and the empty spacer the block inserts when there's no
           subtitle). :has(~ form) keeps the thank-you heading visible. */
        .cf-form-wrap .mh-lead-form-card > h3:has(~ form) {
          display: none;
        }
        .cf-form-wrap .mh-lead-form-card > h3:has(~ form) + div:empty {
          display: none;
        }

        /* Match Header › schedule_tour_link (.nav-cta-btn): solid primary
           fill, 5px radius, surface fill + white label on hover.
           !important beats the multihub-lead-form block's inline styles. */
        .cf-form-wrap .mh-lead-form-submit-button,
        .cf-form-wrap .mh-lead-form-success-cta {
          position: relative;
          overflow: hidden;
          background: var(--vibe-color-primary) !important;
          border: 0 !important;
          border-radius: 5px !important;
          color: #ffffff !important;
          box-shadow: none !important;
          font-weight: 400 !important;
          text-transform: none;
          transition: background-color 0.25s ease, color 0.25s ease,
            box-shadow 0.25s ease, transform 0.25s ease !important;
        }
        .cf-form-wrap .mh-lead-form-submit-button::before,
        .cf-form-wrap .mh-lead-form-success-cta::before {
          content: '';
          position: absolute;
          inset: 0;
          background: transparent;
          opacity: 0;
          transition: opacity 0.25s ease;
        }
        .cf-form-wrap .mh-lead-form-submit-button:hover:not(:disabled),
        .cf-form-wrap .mh-lead-form-success-cta:hover {
          background: var(--vibe-color-surface) !important;
          color: #ffffff !important;
          box-shadow:
            0 2px 6px rgba(0, 0, 0, 0.1),
            0 4px 16px color-mix(in srgb, var(--vibe-color-primary) 30%, transparent) !important;
          transform: translateY(-1px);
        }
        .cf-form-wrap .mh-lead-form-submit-button:hover:not(:disabled)::before,
        .cf-form-wrap .mh-lead-form-success-cta:hover::before {
          opacity: 0;
        }
        .cf-form-wrap .mh-lead-form-submit-button:active:not(:disabled),
        .cf-form-wrap .mh-lead-form-success-cta:active {
          transform: translateY(0);
          box-shadow: none !important;
        }
        .cf-form-wrap .mh-lead-form-success-cta:visited,
        .cf-form-wrap .mh-lead-form-success-cta:focus {
          color: #ffffff !important;
        }
      `}</style>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ContactForm',
  displayName: 'Contact Form',
  tokens: {
    'contactform.form_block': {
      label: 'Form Block',
      type: 'block',
      defaultValue: 'multihub-lead-form',
      acceptedBlockCategories: ['Forms'],
    },
  },
};

(ContactForm as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ContactForm;
